import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDialog, type ConfirmDialogOptions } from '@/contexts/DialogContext';

export const UNSAVED_EQUIPMENT_CATALOG_MESSAGE =
    'You have unsaved equipment catalog changes. Leave without saving?';

const DISCARD_CONFIRM_OPTIONS: ConfirmDialogOptions = {
    title: 'Unsaved changes',
    message: UNSAVED_EQUIPMENT_CATALOG_MESSAGE,
    confirmLabel: 'Leave',
    cancelLabel: 'Stay',
    variant: 'warning',
};

type ConfirmFn = (options: ConfirmDialogOptions) => Promise<boolean>;

/**
 * Returns true if the user confirms discarding unsaved edits (or if there is
 * nothing to discard). The message must NAME what is discarded (product
 * doctrine rule 4 applied to loss: a bare "unsaved changes" tells the user
 * nothing about what they are about to lose); `overrides` lets a caller
 * replace the generic Leave/Stay labels with ones naming the action.
 */
export async function confirmDiscardUnsavedChanges(
    dirty: boolean,
    confirm: ConfirmFn,
    message: string = UNSAVED_EQUIPMENT_CATALOG_MESSAGE,
    overrides?: Partial<ConfirmDialogOptions>,
): Promise<boolean> {
    if (!dirty) return true;
    return confirm({
        ...DISCARD_CONFIRM_OPTIONS,
        message,
        ...overrides,
    });
}

/**
 * Warns before browser unload and before in-app link navigations while `dirty`.
 * Use alongside explicit confirms for non-route UI switches (office picker, tabs).
 *
 * Note: `beforeunload` must use the browser’s native prompt; custom modals are not allowed there.
 */
export function useUnsavedChangesGuard(
    dirty: boolean,
    message: string = UNSAVED_EQUIPMENT_CATALOG_MESSAGE,
): void {
    const { confirm } = useDialog();
    const navigate = useNavigate();

    useEffect(() => {
        if (!dirty) return;

        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };

        const onDocumentClick = (event: MouseEvent) => {
            if (event.defaultPrevented) return;
            if (event.button !== 0) return;
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

            const anchor = (event.target as Element | null)?.closest?.('a[href]');
            if (!anchor) return;

            const href = anchor.getAttribute('href');
            if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
                return;
            }

            // External absolute URLs — let the browser handle (beforeunload covers hard nav).
            if (/^[a-z][a-z0-9+.-]*:/i.test(href)) return;

            event.preventDefault();
            event.stopPropagation();

            void (async () => {
                const ok = await confirm({
                    ...DISCARD_CONFIRM_OPTIONS,
                    message,
                });
                if (ok) {
                    navigate(href);
                }
            })();
        };

        window.addEventListener('beforeunload', onBeforeUnload);
        document.addEventListener('click', onDocumentClick, true);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            document.removeEventListener('click', onDocumentClick, true);
        };
    }, [dirty, message, confirm, navigate]);
}
